import { configs } from "./configs";

export type RoyaltyFreeMusicProvider = "preset" | "jamendo" | "wikimedia";

export interface RoyaltyFreeMusicTrack {
	id: string;
	title: string;
	url: string;
	provider: RoyaltyFreeMusicProvider;
	attribution: string;
	sourcePageUrl?: string;
	durationMs?: number;
	/** Short mood tags for auto-pick / filters. */
	moods?: string[];
}

/**
 * Curated instrumental beds hosted on Wikimedia Commons (Kevin MacLeod, CC BY 3.0).
 * URLs are upload.wikimedia.org — verified publicly fetchable (no DesignCombo CDN).
 */
export const VIDEO_PROJECT_MUSIC_PRESETS: RoyaltyFreeMusicTrack[] = [
	{
		id: "preset_darkest_child",
		title: "Darkest Child",
		url: "https://upload.wikimedia.org/wikipedia/commons/f/fe/Darkest_Child_%28ISRC_USUAN1100783%29.mp3",
		provider: "wikimedia",
		attribution: "Kevin MacLeod (incompetech.com) — CC BY 3.0 via Wikimedia Commons",
		sourcePageUrl:
			"https://commons.wikimedia.org/wiki/File:Darkest_Child_(ISRC_USUAN1100783).mp3",
		moods: [
			"dark",
			"suspense",
			"crime",
			"mystery",
			"tense",
			"thriller",
			"horror",
			"noir",
			"cinematic",
		],
		durationMs: 239_000,
	},
	{
		id: "preset_investigations",
		title: "Investigations",
		url: "https://upload.wikimedia.org/wikipedia/commons/8/83/Investigations_%28ISRC_USUAN1100646%29.mp3",
		provider: "wikimedia",
		attribution: "Kevin MacLeod (incompetech.com) — CC BY 3.0 via Wikimedia Commons",
		sourcePageUrl:
			"https://commons.wikimedia.org/wiki/File:Investigations_(ISRC_USUAN1100646).mp3",
		moods: ["comedy", "quirky", "fun", "humorous", "light", "satire"],
		durationMs: 94_000,
	},
	{
		id: "preset_long_note_two",
		title: "Long Note Two",
		url: "https://upload.wikimedia.org/wikipedia/commons/5/50/Long_Note_Two_%28ISRC_USUAN1100420%29.mp3",
		provider: "wikimedia",
		attribution: "Kevin MacLeod (incompetech.com) — CC BY 3.0 via Wikimedia Commons",
		sourcePageUrl:
			"https://commons.wikimedia.org/wiki/File:Long_Note_Two_(ISRC_USUAN1100420).mp3",
		moods: ["dark", "somber", "atmospheric", "tense", "drama", "tragedy"],
		durationMs: 462_000,
	},
	{
		id: "preset_dreams_become_real",
		title: "Dreams Become Real",
		url: "https://upload.wikimedia.org/wikipedia/commons/c/cd/Dreams_Become_Real_%28ISRC_USUAN1500027%29.mp3",
		provider: "wikimedia",
		attribution: "Kevin MacLeod (incompetech.com) — CC BY 3.0 via Wikimedia Commons",
		sourcePageUrl:
			"https://commons.wikimedia.org/wiki/File:Dreams_Become_Real_(ISRC_USUAN1500027).mp3",
		moods: ["hopeful", "cinematic", "story", "uplifting", "inspirational"],
		durationMs: 240_000,
	},
	{
		id: "preset_evening_fall",
		title: "Evening Fall (Piano)",
		url: "https://upload.wikimedia.org/wikipedia/commons/f/fd/Evening_Fall_%28Piano%29_%28ISRC_USUAN1100235%29.mp3",
		provider: "wikimedia",
		attribution: "Kevin MacLeod (incompetech.com) — CC BY 3.0 via Wikimedia Commons",
		sourcePageUrl:
			"https://commons.wikimedia.org/wiki/File:Evening_Fall_(Piano)_(ISRC_USUAN1100235).mp3",
		moods: ["gentle", "emotional", "story", "piano", "tender", "romance"],
		durationMs: 180_000,
	},
	{
		id: "preset_carefree",
		title: "Carefree",
		url: "https://upload.wikimedia.org/wikipedia/commons/f/f5/Carefree_%28ISRC_USUAN1400037%29.mp3",
		provider: "wikimedia",
		attribution: "Kevin MacLeod (incompetech.com) — CC BY 3.0 via Wikimedia Commons",
		sourcePageUrl:
			"https://commons.wikimedia.org/wiki/File:Carefree_(ISRC_USUAN1400037).mp3",
		moods: ["warm", "uplifting", "story", "light", "happy"],
		durationMs: 180_000,
	},
	{
		id: "preset_piano_between",
		title: "Piano Between",
		url: "https://upload.wikimedia.org/wikipedia/commons/6/6c/Piano_Between_%28ISRC_USUAN1100490%29.mp3",
		provider: "wikimedia",
		attribution: "Kevin MacLeod (incompetech.com) — CC BY 3.0 via Wikimedia Commons",
		sourcePageUrl:
			"https://commons.wikimedia.org/wiki/File:Piano_Between_(ISRC_USUAN1100490).mp3",
		moods: ["documentary", "calm", "piano", "neutral", "news"],
		durationMs: 60_000,
	},
];

const DEFAULT_BGM_VOLUME = 17;

export function defaultBackgroundMusicVolume(): number {
	return DEFAULT_BGM_VOLUME;
}

/** Content → mood weights used to score curated presets. */
const CONTENT_MOOD_SIGNALS: Array<{
	pattern: RegExp;
	moods: string[];
	weight: number;
}> = [
	{
		pattern:
			/\b(crime|criminal|murder|homicide|heist|robbery|theft|fraud|scam|kidnap|abduct|assassin|mafia|cartel|gang|corrupt|corruption|prison|jail|fugitive|escape|conspiracy|noir|detective|forensic|cold\s*case|whodunit|thriller|suspense|mystery|mysterious|horror|terror|true\s*crime|dark\s*web|blackmail|ransom|smuggl|traffick)\b/i,
		moods: [
			"crime",
			"horror",
			"mystery",
			"suspense",
			"dark",
			"tense",
			"thriller",
			"noir",
		],
		weight: 12,
	},
	{
		pattern:
			/\b(war|battle|conflict|attack|violence|blood|death|tragic|tragedy|disaster|crisis|collapse|scandal|cover[- ]?up)\b/i,
		moods: ["dark", "somber", "tense", "drama", "tragedy"],
		weight: 9,
	},
	{
		pattern:
			/\b(love|romance|romantic|family|hope|heart|emotional|tender|wedding|compassion)\b/i,
		moods: ["gentle", "emotional", "tender", "romance", "piano", "hopeful"],
		weight: 8,
	},
	{
		pattern:
			/\b(comedy|comic|comedic|funny|humor|humour|humorous|satire|parody|slapstick|quirky|witty)\b/i,
		moods: ["comedy", "quirky", "fun", "humorous", "light", "satire"],
		weight: 10,
	},
	{
		pattern:
			/\b(inspire|inspirational|uplift|triumph|success|celebration|joyful|happy|fun|kids|children|adventure)\b/i,
		moods: ["uplifting", "hopeful", "warm", "light", "happy", "story"],
		weight: 7,
	},
	{
		pattern:
			/\b(docu|documentary|news|report|history|histori|migration|journalis|exposé|expose)\b/i,
		moods: ["documentary", "calm", "neutral", "news"],
		weight: 5,
	},
];

function findPresetById(id: string): RoyaltyFreeMusicTrack | undefined {
	return VIDEO_PROJECT_MUSIC_PRESETS.find((track) => track.id === id);
}

function fallbackPresetForFormat(format?: string): RoyaltyFreeMusicTrack {
	if (format === "documentary" || format === "news")
		return (
			findPresetById("preset_piano_between") || VIDEO_PROJECT_MUSIC_PRESETS[0]!
		);
	return findPresetById("preset_carefree") || VIDEO_PROJECT_MUSIC_PRESETS[0]!;
}

const CRIME_HORROR_PATTERN =
	/\b(crime|criminal|murder|homicide|heist|robbery|theft|fraud|scam|kidnap|abduct|assassin|mafia|cartel|gang|corrupt|corruption|prison|jail|fugitive|escape|conspiracy|noir|detective|forensic|cold\s*case|whodunit|thriller|suspense|mystery|mysterious|horror|terror|true\s*crime|dark\s*web|blackmail|ransom|smuggl|traffick)\b/i;

/**
 * Pick a curated preset from format / title / logline (and optional extra text).
 * Crime/horror → Darkest Child; comedy → Investigations; otherwise score by mood.
 */
export function autoSelectBackgroundMusicPreset(input: {
	format?: string;
	title?: string;
	logline?: string;
	theme?: string;
	/** Extra free text (e.g. beat titles) to improve mood matching. */
	extraText?: string;
}): RoyaltyFreeMusicTrack {
	const haystack =
		`${input.format || ""} ${input.title || ""} ${input.logline || ""} ${input.theme || ""} ${input.extraText || ""}`.toLowerCase();

	/** Hard rule: crime / horror / true-crime beds use Darkest Child. */
	if (CRIME_HORROR_PATTERN.test(haystack)) {
		return (
			findPresetById("preset_darkest_child") || VIDEO_PROJECT_MUSIC_PRESETS[0]!
		);
	}

	const desiredMoodWeights = new Map<string, number>();
	for (const signal of CONTENT_MOOD_SIGNALS) {
		if (!signal.pattern.test(haystack)) continue;
		for (const mood of signal.moods) {
			desiredMoodWeights.set(
				mood,
				(desiredMoodWeights.get(mood) || 0) + signal.weight,
			);
		}
	}

	if (input.format === "documentary" || input.format === "news") {
		desiredMoodWeights.set(
			"documentary",
			(desiredMoodWeights.get("documentary") || 0) + 3,
		);
		desiredMoodWeights.set(
			"news",
			(desiredMoodWeights.get("news") || 0) + 2,
		);
		desiredMoodWeights.set("calm", (desiredMoodWeights.get("calm") || 0) + 2);
	}

	if (desiredMoodWeights.size === 0)
		return fallbackPresetForFormat(input.format);

	let best: RoyaltyFreeMusicTrack | null = null;
	let bestScore = Number.NEGATIVE_INFINITY;

	for (const track of VIDEO_PROJECT_MUSIC_PRESETS) {
		let score = 0;
		for (const mood of track.moods || []) {
			score += desiredMoodWeights.get(mood) || 0;
		}
		if (score > bestScore) {
			bestScore = score;
			best = track;
		}
	}

	if (!best || bestScore <= 0) return fallbackPresetForFormat(input.format);
	return best;
}

interface JamendoTrack {
	id: string;
	name?: string;
	duration?: number;
	audio?: string;
	audiodownload?: string;
	shareurl?: string;
	artist_name?: string;
	album_name?: string;
}

interface JamendoResponse {
	results?: JamendoTrack[];
	headers?: { status?: string; error_message?: string };
}

/**
 * Search Jamendo (Creative Commons / royalty-free friendly streaming).
 * Requires `JAMENDO_CLIENT_ID` in env.
 */
export async function searchJamendoMusic(
	query: string,
	options?: { limit?: number },
): Promise<RoyaltyFreeMusicTrack[]> {
	const clientId = configs.jamendo?.clientId?.trim();
	if (!clientId) return [];

	const trimmed = query.trim();
	if (!trimmed) return [];

	const limit = Math.min(30, Math.max(1, options?.limit ?? 15));
	const params = new URLSearchParams({
		client_id: clientId,
		format: "json",
		limit: String(limit),
		search: trimmed,
		audioformat: "mp32",
		include: "musicinfo",
		order: "relevance",
	});

	const response = await fetch(
		`https://api.jamendo.com/v3.0/tracks/?${params.toString()}`,
		{ headers: { Accept: "application/json" } },
	);
	if (!response.ok) {
		throw new Error(`Jamendo API error: ${response.status}`);
	}

	const data = (await response.json()) as JamendoResponse;
	if (data.headers?.status === "failed") {
		throw new Error(data.headers.error_message || "Jamendo search failed");
	}

	const tracks: RoyaltyFreeMusicTrack[] = [];
	for (const track of data.results || []) {
		const url = (track.audio || track.audiodownload || "").trim();
		if (!url.startsWith("https://")) continue;
		const durationSec =
			typeof track.duration === "number" && track.duration > 0
				? track.duration
				: undefined;
		tracks.push({
			id: `jamendo_${track.id}`,
			title: track.name?.trim() || `Track ${track.id}`,
			url,
			provider: "jamendo",
			attribution: [track.artist_name, track.album_name]
				.filter(Boolean)
				.join(" — ") || "Jamendo",
			sourcePageUrl: track.shareurl || "",
			durationMs: durationSec ? Math.round(durationSec * 1000) : undefined,
		});
	}
	return tracks;
}

const COMMONS_API = "https://commons.wikimedia.org/w/api.php";

interface CommonsAudioPage {
	pageid: number;
	title: string;
	imageinfo?: Array<{
		url?: string;
		descriptionurl?: string;
		mime?: string;
		duration?: number;
		user?: string;
		extmetadata?: Record<string, { value?: string }>;
	}>;
}

/**
 * Search Wikimedia Commons for royalty-free audio files (ogg/mp3/wav).
 * No API key — User-Agent only.
 */
export async function searchWikimediaMusic(
	query: string,
	options?: { limit?: number },
): Promise<RoyaltyFreeMusicTrack[]> {
	const trimmed = query.trim();
	if (!trimmed) return [];

	const limit = Math.min(30, Math.max(1, options?.limit ?? 12));
	const params = new URLSearchParams({
		action: "query",
		format: "json",
		origin: "*",
		generator: "search",
		gsrnamespace: "6",
		gsrlimit: String(limit),
		gsrsearch: `filetype:audio|ogg|flac ${trimmed}`,
		prop: "imageinfo",
		iiprop: "url|size|mime|user|extmetadata|duration",
	});

	const response = await fetch(`${COMMONS_API}?${params.toString()}`, {
		headers: {
			"User-Agent":
				configs.wikimedia?.userAgent || "SwiftiaVideoProject/1.0",
			Accept: "application/json",
		},
	});
	if (!response.ok) {
		throw new Error(`Wikimedia audio search error: ${response.status}`);
	}

	const data = (await response.json()) as {
		query?: { pages?: Record<string, CommonsAudioPage> };
	};
	const tracks: RoyaltyFreeMusicTrack[] = [];
	for (const page of Object.values(data.query?.pages || {})) {
		const info = page.imageinfo?.[0];
		const url = info?.url?.trim() || "";
		const mime = info?.mime || "";
		if (!url.startsWith("https://")) continue;
		if (mime && !mime.startsWith("audio/") && mime !== "application/ogg")
			continue;
		const artist =
			info?.extmetadata?.Artist?.value?.replace(/<[^>]+>/g, "").trim() ||
			info?.user ||
			"Wikimedia Commons";
		const title = page.title.replace(/^File:/, "");
		const durationSec =
			typeof info?.duration === "number" && info.duration > 0
				? info.duration
				: undefined;
		tracks.push({
			id: `wikimedia_audio_${page.pageid}`,
			title,
			url,
			provider: "wikimedia",
			attribution: `${title} — ${artist} (Wikimedia Commons)`,
			sourcePageUrl: info?.descriptionurl || "",
			durationMs: durationSec ? Math.round(durationSec * 1000) : undefined,
		});
	}
	return tracks;
}

/**
 * Unified royalty-free search: Jamendo (if configured) + Wikimedia Commons audio.
 */
export async function searchRoyaltyFreeMusic(
	query: string,
	options?: { limit?: number },
): Promise<{
	tracks: RoyaltyFreeMusicTrack[];
	providers: RoyaltyFreeMusicProvider[];
	jamendoConfigured: boolean;
}> {
	const limit = options?.limit ?? 15;
	const jamendoConfigured = Boolean(configs.jamendo?.clientId?.trim());
	const providers: RoyaltyFreeMusicProvider[] = [];
	const [jamendo, wikimedia] = await Promise.all([
		jamendoConfigured
			? searchJamendoMusic(query, { limit }).catch(() => [] as RoyaltyFreeMusicTrack[])
			: Promise.resolve([] as RoyaltyFreeMusicTrack[]),
		searchWikimediaMusic(query, { limit: Math.min(12, limit) }).catch(
			() => [] as RoyaltyFreeMusicTrack[],
		),
	]);
	if (jamendo.length > 0) providers.push("jamendo");
	if (wikimedia.length > 0) providers.push("wikimedia");

	const seen = new Set<string>();
	const tracks: RoyaltyFreeMusicTrack[] = [];
	for (const track of [...jamendo, ...wikimedia]) {
		if (seen.has(track.url)) continue;
		seen.add(track.url);
		tracks.push(track);
		if (tracks.length >= limit) break;
	}

	return { tracks, providers, jamendoConfigured };
}
